import React, { useState, useEffect } from 'react';
import {
  buildSharePageUrl,
  getProjectShareStatus,
  enableProjectShare,
  disableProjectShare,
} from '../../services/api';

function TrackLockIcon() {
  return (
    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2.2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function TrackGlobeIcon() {
  return (
    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
      <path d="M3 12h18M12 3c2.5 2.8 2.5 14.2 0 18M12 3c-2.5 2.8-2.5 14.2 0 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

export default function ShareModal({ projectId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [isShared, setIsShared] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    getProjectShareStatus(projectId)
      .then((data) => {
        if (!mounted) return;
        setIsShared(Boolean(data.isShared));
        setShareUrl(data.shareToken ? buildSharePageUrl(data.shareToken) : '');
      })
      .catch((e) => {
        if (mounted) setError(e.message);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [projectId]);

  const handleToggle = async () => {
    if (toggling || loading) return;
    setToggling(true);
    setError('');
    try {
      if (isShared) {
        await disableProjectShare(projectId);
        setIsShared(false);
        setShareUrl('');
      } else {
        const data = await enableProjectShare(projectId);
        setIsShared(true);
        setShareUrl(buildSharePageUrl(data.shareToken));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setToggling(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  const handlePrimary = () => {
    if (isShared && shareUrl) {
      handleCopy();
    } else {
      handleToggle();
    }
  };

  const busy = loading || toggling;

  return (
    <div style={s.backdrop} onClick={onClose} role="presentation">
      <div
        style={s.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="share-modal-title"
        aria-modal="true"
      >
        <header style={s.header}>
          <h2 id="share-modal-title" style={s.title}>Share Project</h2>
          <p style={s.subtitle}>Generate a public link to share this project.</p>
        </header>

        <div style={s.body}>
          <section style={s.section}>
            <div style={s.accessRow}>
              <div style={s.accessText}>
                <h3 style={s.sectionLabel}>Public Sharing</h3>
                <p style={s.sectionDesc}>Anyone with the link can view this project.</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isShared}
                aria-label={isShared ? 'Public sharing enabled' : 'Public sharing disabled'}
                disabled={busy}
                onClick={handleToggle}
                className={[
                  'share-premium-toggle',
                  isShared ? 'share-premium-toggle--on' : 'share-premium-toggle--off',
                  busy ? 'share-premium-toggle--busy' : '',
                ].filter(Boolean).join(' ')}
              >
                <span className="share-premium-toggle__track">
                  <span className="share-premium-toggle__icon share-premium-toggle__icon--lock">
                    <TrackLockIcon />
                  </span>
                  <span className="share-premium-toggle__icon share-premium-toggle__icon--globe">
                    <TrackGlobeIcon />
                  </span>
                  <span className="share-premium-toggle__thumb" />
                </span>
              </button>
            </div>
          </section>

          {isShared && shareUrl && (
            <section style={s.section} className="share-modal-link-section">
              <h3 style={s.sectionLabel}>Share Link</h3>
              <div style={s.urlRow}>
                <input
                  readOnly
                  value={shareUrl}
                  style={s.urlInput}
                  aria-label="Share link"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  style={{
                    ...s.inlineBtn,
                    ...(copied ? s.inlineBtnSuccess : s.inlineBtnPrimary),
                  }}
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
                <button
                  type="button"
                  onClick={() => window.open(shareUrl, '_blank', 'noopener,noreferrer')}
                  style={s.inlineBtnOutline}
                >
                  Open Link
                </button>
              </div>
            </section>
          )}

          <ul style={s.infoList}>
            <li style={s.infoItem}>✓ View project preview</li>
            <li style={s.infoItem}>✓ Browse generated code</li>
            <li style={s.infoItem}>✓ No login required</li>
          </ul>

          {error && <p style={s.error}>{error}</p>}
        </div>

        <footer style={s.footer}>
          <span style={s.footerStatus}>
            {isShared ? 'Public link active' : 'Sharing disabled'}
          </span>
          <div style={s.footerActions}>
            <button type="button" onClick={onClose} style={s.footerClose}>
              Close
            </button>
            <button
              type="button"
              onClick={handlePrimary}
              disabled={busy || (isShared && !shareUrl)}
              style={{
                ...s.footerPrimary,
                ...(busy ? s.footerPrimaryDisabled : {}),
              }}
            >
              {isShared ? 'Copy Link' : 'Enable Sharing'}
            </button>
          </div>
        </footer>
      </div>

      <style>{`
        .share-premium-toggle {
          position: relative;
          flex-shrink: 0;
          padding: 0;
          border: none;
          background: transparent;
          cursor: pointer;
          border-radius: 999px;
        }

        .share-premium-toggle--busy {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .share-premium-toggle:focus-visible {
          outline: none;
        }

        .share-premium-toggle:focus-visible .share-premium-toggle__track {
          box-shadow: 0 0 0 2px var(--accent);
        }

        .share-premium-toggle__track {
          position: relative;
          display: block;
          width: 50px;
          height: 25px;
          border-radius: 999px;
          transition:
            background 0.24s ease,
            border-color 0.24s ease,
            box-shadow 0.24s ease;
        }

        /* Light — OFF */
        [data-theme="light"] .share-premium-toggle--off .share-premium-toggle__track {
          background: #E6EAF0;
          border: 1px solid #C9D1DC;
        }

        [data-theme="light"] .share-premium-toggle--off:hover:not(.share-premium-toggle--busy) .share-premium-toggle__track {
          background: #DDE2EA;
          border-color: #B8C2D0;
        }

        /* Light — ON */
        [data-theme="light"] .share-premium-toggle--on .share-premium-toggle__track {
          background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
          border: 1px solid rgba(99, 102, 241, 0.5);
          box-shadow: 0 0 12px rgba(99, 102, 241, 0.28);
        }

        [data-theme="light"] .share-premium-toggle--on:hover:not(.share-premium-toggle--busy) .share-premium-toggle__track {
          background: linear-gradient(135deg, #5558e8 0%, #6d28d9 100%);
        }

        /* Dark — OFF */
        [data-theme="dark"] .share-premium-toggle--off .share-premium-toggle__track,
        :root:not([data-theme="light"]) .share-premium-toggle--off .share-premium-toggle__track {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }

        [data-theme="dark"] .share-premium-toggle--off:hover:not(.share-premium-toggle--busy) .share-premium-toggle__track,
        :root:not([data-theme="light"]) .share-premium-toggle--off:hover:not(.share-premium-toggle--busy) .share-premium-toggle__track {
          background: rgba(255, 255, 255, 0.11);
          border-color: rgba(255, 255, 255, 0.16);
        }

        /* Dark — ON */
        [data-theme="dark"] .share-premium-toggle--on .share-premium-toggle__track,
        :root:not([data-theme="light"]) .share-premium-toggle--on .share-premium-toggle__track {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          border: 1px solid rgba(129, 140, 248, 0.45);
          box-shadow: 0 0 14px rgba(99, 102, 241, 0.35);
        }

        [data-theme="dark"] .share-premium-toggle--on:hover:not(.share-premium-toggle--busy) .share-premium-toggle__track,
        :root:not([data-theme="light"]) .share-premium-toggle--on:hover:not(.share-premium-toggle--busy) .share-premium-toggle__track {
          background: linear-gradient(135deg, #5558e8 0%, #7c3aed 100%);
        }

        .share-premium-toggle__thumb {
          position: absolute;
          top: 3px;
          left: 0;
          width: 19px;
          height: 19px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
          transform: translateX(3px);
          transition:
            transform 0.24s ease,
            box-shadow 0.24s ease;
          pointer-events: none;
        }

        .share-premium-toggle--on .share-premium-toggle__thumb {
          transform: translateX(28px);
          box-shadow:
            0 1px 4px rgba(0, 0, 0, 0.16),
            0 0 0 1px rgba(255, 255, 255, 0.08);
        }

        .share-premium-toggle:hover:not(.share-premium-toggle--busy) .share-premium-toggle__thumb {
          transform: translateX(3px) translateY(-1px);
        }

        .share-premium-toggle--on:hover:not(.share-premium-toggle--busy) .share-premium-toggle__thumb {
          transform: translateX(28px) translateY(-1px);
        }

        .share-premium-toggle__icon {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          transition: opacity 0.24s ease;
        }

        .share-premium-toggle__icon--lock {
          left: 7px;
          color: #5B6472;
          opacity: 0.55;
        }

        .share-premium-toggle__icon--globe {
          right: 7px;
          color: rgba(255, 255, 255, 0.85);
          opacity: 0;
        }

        .share-premium-toggle--on .share-premium-toggle__icon--lock {
          opacity: 0;
        }

        .share-premium-toggle--on .share-premium-toggle__icon--globe {
          opacity: 0.7;
        }

        [data-theme="dark"] .share-premium-toggle--off .share-premium-toggle__icon--lock,
        :root:not([data-theme="light"]) .share-premium-toggle--off .share-premium-toggle__icon--lock {
          color: #94a3b8;
        }

        .share-modal-link-section {
          animation: shareLinkIn 0.22s ease-out;
        }

        @keyframes shareLinkIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

const s = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    zIndex: 1100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    width: '100%',
    maxWidth: 560,
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-card)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 24px 0',
  },
  title: {
    fontSize: 17,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: 0,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    margin: '4px 0 0',
    lineHeight: 1.45,
  },
  body: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  accessRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 20,
  },
  accessText: {
    flex: 1,
    minWidth: 0,
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
  },
  sectionDesc: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    margin: '3px 0 0',
    lineHeight: 1.45,
  },
  urlRow: {
    display: 'flex',
    gap: 8,
    marginTop: 4,
  },
  urlInput: {
    flex: 1,
    minWidth: 0,
    padding: '8px 10px',
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-primary)',
    fontSize: 12,
    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
    outline: 'none',
  },
  inlineBtn: {
    padding: '8px 12px',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    fontWeight: 600,
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 0.15s ease, color 0.15s ease',
  },
  inlineBtnPrimary: {
    background: 'var(--accent)',
    color: '#fff',
  },
  inlineBtnSuccess: {
    background: 'rgba(34,197,94,0.15)',
    color: 'var(--success)',
  },
  inlineBtnOutline: {
    padding: '8px 12px',
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    fontWeight: 600,
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'border-color 0.15s ease, color 0.15s ease',
  },
  infoList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    borderTop: '1px solid var(--border)',
    paddingTop: 16,
  },
  infoItem: {
    fontSize: 12,
    color: 'var(--text-muted)',
    lineHeight: 1.4,
  },
  error: {
    fontSize: 12,
    color: 'var(--error)',
    margin: 0,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '14px 24px',
    borderTop: '1px solid var(--border)',
    background: 'var(--bg-card)',
  },
  footerStatus: {
    fontSize: 12,
    color: 'var(--text-muted)',
    fontWeight: 500,
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  footerClose: {
    padding: '7px 14px',
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  footerPrimary: {
    padding: '7px 16px',
    background: 'var(--accent)',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
  },
  footerPrimaryDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
};
